import React, { useState } from 'react';
import { Compass, AlertTriangle, Clock, Phone, Loader2, CheckCircle2, Shield } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import VillaRenderer from '@/components/VillaRenderer';
import { useLanguage } from '@/context/LanguageContext';
import translations from '@/data/translations';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VaastuClaimModal({ plot, open, onOpenChange }) {
  const { lang } = useLanguage();
  const t = translations[lang].vaastu;
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  if (!plot) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !phone.trim()) { setError(t.errorRequired); return; }
    if (!/^[6-9]\d{9}$/.test(phone.replace(/\s/g, ''))) { setError(t.errorPhone); return; }
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${API}/vaastu-claims`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim(), phone: phone.trim(), plot_id: plot.id }),
      });
      if (res.ok) {
        const claimData = await res.json();
        setSuccess(true);
        // Fire WhatsApp alert to ground partner
        const alertMsg = encodeURIComponent(`🚨 HIGH INTENT: ${name} (${phone}) wants to outbid/claim Vaastu Plot #${plot.id}. Call within 5 minutes!`);
        window.open(`https://wa.me/919403890991?text=${alertMsg}`, '_blank');
        // Auto-send WhatsApp confirmation to buyer
        setTimeout(() => {
          const buyerMsg = encodeURIComponent(`Hi ${name}, your priority claim for Vaastu Plot #${plot.id} is being reviewed. Our team will call you shortly. - Lakshmi Nivasa`);
          window.open(`https://wa.me/91${phone.replace(/\s/g, '')}?text=${buyerMsg}`, '_blank');
        }, 1500);
      } else setError(t.errorGeneric);
    } catch { setError(t.errorGeneric); }
    finally { setLoading(false); }
  };

  const handleClose = () => {
    onOpenChange(false);
    setTimeout(() => { setSuccess(false); setName(''); setPhone(''); setError(''); }, 300);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        data-testid="vaastu-claim-modal"
        className="sm:max-w-lg bg-[#FDFBF7] border-amber-200 rounded-2xl max-h-[90vh] overflow-y-auto"
      >
        {success ? (
          <div className="text-center py-6 space-y-4" data-testid="vaastu-claim-success">
            <CheckCircle2 className="w-14 h-14 text-[#2C4C3B] mx-auto" />
            <h3 className="font-serif text-xl font-bold text-[#1A2520]">{t.successTitle}</h3>
            <p className="text-[#5C6A61] text-sm">{t.successMsg}</p>
            <button onClick={handleClose} className="px-6 py-2.5 bg-[#2C4C3B] text-white rounded-xl text-sm font-medium">
              {t.close}
            </button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-serif text-lg text-[#1A2520] flex items-center gap-2">
                <Shield className="w-5 h-5 text-amber-500" />
                {t.modalTitle} <span className="text-[#D48C57]">#{plot.id}</span>
              </DialogTitle>
            </DialogHeader>

            {/* Urgency Alert */}
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-3">
              <div className="flex items-start gap-2">
                <Clock className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-amber-800 text-xs font-bold uppercase tracking-wider">{t.holdStatus}</p>
                  <p className="text-amber-700 text-sm mt-1 leading-relaxed">{t.urgencyText.replace('[PLOT]', `#${plot.id}`)}</p>
                </div>
              </div>

              {/* Vaastu compass + villa preview */}
              <div className="flex items-center gap-3 bg-white rounded-lg p-3 border border-amber-100">
                <div className="flex-shrink-0">
                  <Compass className="w-10 h-10 text-amber-500" />
                </div>
                <div className="text-xs text-[#5C6A61]">
                  <p className="font-semibold text-[#1A2520]">{t.vaastuLabel}</p>
                  <p>{plot.area.toLocaleString()} sq.ft | {plot.facing}</p>
                </div>
              </div>
            </div>

            {/* Priority Claim Form */}
            <form onSubmit={handleSubmit} className="space-y-4 mt-2">
              <div className="space-y-1.5">
                <label className="text-[#1A2520] text-xs font-medium">{t.nameLabel}</label>
                <Input data-testid="vaastu-name" value={name} onChange={(e) => setName(e.target.value)}
                  placeholder={t.namePlaceholder} className="border-[#E5DFD3] focus-visible:ring-amber-500 h-11" />
              </div>
              <div className="space-y-1.5">
                <label className="text-[#1A2520] text-xs font-medium">{t.phoneLabel}</label>
                <Input data-testid="vaastu-phone" value={phone} onChange={(e) => setPhone(e.target.value)}
                  placeholder={t.phonePlaceholder} type="tel" className="border-[#E5DFD3] focus-visible:ring-amber-500 h-11" />
              </div>
              {error && <p className="text-red-500 text-xs">{error}</p>}
              <button type="submit" disabled={loading} data-testid="vaastu-submit"
                className="w-full flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-600 disabled:opacity-60 text-white py-3.5 rounded-xl text-sm font-bold transition-all duration-200 shadow-lg shadow-amber-500/20">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <AlertTriangle className="w-4 h-4" />}
                {loading ? t.submitting : t.submitBtn}
              </button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
